import { ENV_VARS } from '@/env';
import { isNumber } from '@/utils/number';
import { isEmpty } from '@/utils/object';
import { Col, Row } from 'antd';
import { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReactComponent as DeleteIcon } from '../../../../../../assets/icons/delete-icon.svg';
import { APP_ROUTES } from '../../../../../../contants/routes';
import {
  getCurrentQuoteCollaboratorsList,
  removeCurrentQuoteCollaborators,
  resetCurrentQuoteCollaborators,
  setCurrentQuoteCollaborators,
} from '../../../../../../redux/common/CommonActions';
import {
  selectCurrentQuoteCollaborators,
  selectCurrentQuoteRoutingGuidesParams,
} from '../../../../../../redux/common/CommonReducer';
import Button from '../../../../../components/Buttons/Button';
import IconButton from '../../../../../components/Buttons/IconButton';
import SvgConverter from '../../../../../components/Common/SvgConverter';
import FormCheckBox from '../../../../../components/Inputs/FormCheckBox';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import Drawer from '../../../../../components/Modals/Drawer';
import CollaboratorsModal from '../../common/CollaboratorsModal';
import HalQuoteCollaboratorsModal from '../../common/HalQuoteCollaboratorsModal';

const QuoteCollaboratorLogs = () => {
  const [visible, setVisible] = useState(false);
  const [checkedCollaboratorsIds, setCheckedCollaboratorsIds] = useState([]);

  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const prevParamsRef = useRef();
  const isHBUser = ENV_VARS.REACT_APP_THEME === 'halliburton';
  const isCreateQuote = pathname === APP_ROUTES.SEARCH_QUOTE_CREATE;

  const currentQuoteRoutingGuidesParams = useSelector(selectCurrentQuoteRoutingGuidesParams);
  const copyQuoteKey = useSelector((state) => state.common.copyQuoteKey);
  const collaborators = useSelector(selectCurrentQuoteCollaborators);
  const { collaborators: collaboratorsData } = useSelector((state) => state.shipment.spotQuoteCollaborators.data);
  const CollaboratorsModalComponent = isHBUser ? HalQuoteCollaboratorsModal : CollaboratorsModal;

  useEffect(() => {
    dispatch(resetCurrentQuoteCollaborators());
  }, [dispatch]);

  // Copy collaborators if it is a copy of a quote
  useEffect(() => {
    if (copyQuoteKey && collaboratorsData?.length) {
      prevParamsRef.current = currentQuoteRoutingGuidesParams;
      dispatch(
        setCurrentQuoteCollaborators(
          collaboratorsData.map((item) => ({
            name: item?.userName,
            email: item?.emails,
            role: item?.role,
            notificationsEnabled: item?.notificationsEnabled,
            group: item?.group,
            userId: item?.userId,
          })),
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { CountryCode, TransportationMode, TransportationGroup } = currentQuoteRoutingGuidesParams;

    if (
      CountryCode &&
      TransportationMode &&
      TransportationGroup &&
      !(isEmpty(prevParamsRef.current) && copyQuoteKey) &&
      (!prevParamsRef.current ||
        prevParamsRef.current.CountryCode !== CountryCode ||
        prevParamsRef.current.TransportationMode !== TransportationMode ||
        prevParamsRef.current.TransportationGroup !== TransportationGroup)
    ) {
      dispatch(getCurrentQuoteCollaboratorsList());
    }

    prevParamsRef.current = currentQuoteRoutingGuidesParams;
  }, [currentQuoteRoutingGuidesParams]);

  const handleCloseDrawer = () => {
    setVisible(false);
  };

  const handleCheckCollaborators = (e, checkedId) => {
    if (e.target.checked) {
      setCheckedCollaboratorsIds([...checkedCollaboratorsIds, checkedId]);
    } else {
      setCheckedCollaboratorsIds(checkedCollaboratorsIds.filter((id) => id !== checkedId));
    }
  };

  const handleDeleteCollaborators = (collaboratorId) => {
    const selected = isNumber(collaboratorId) ? [collaboratorId] : checkedCollaboratorsIds;
    dispatch(removeCurrentQuoteCollaborators(selected));
    setCheckedCollaboratorsIds([]);
  };

  return (
    <div className="h-100 custom-scroll-bar">
      <Row justify="end" align="middle">
        <Col className="px-15 py-10">
          <Row gutter={15}>
            <Col>
              <Button dataTestId="quote-collborator-export-button" className={isHBUser ? 'hb-button' : ''}>
                Export to Excel
              </Button>
            </Col>

            <Col>
              <Button
                dataTestId="quote-collborator-add-user-button"
                className={isHBUser ? 'hb-button' : ''}
                onClick={() => setVisible(true)}
              >
                Add Users
              </Button>

              <Drawer
                width="70%"
                visible={visible}
                headerContents={<PrimaryLabel className="text-white">Collaborator Search</PrimaryLabel>}
                onClose={() => setVisible(false)}
              >
                <CollaboratorsModalComponent
                  createQuote={isCreateQuote}
                  collaborators={collaborators}
                  onCloseModal={handleCloseDrawer}
                  handleClose={handleCloseDrawer}
                />
              </Drawer>
            </Col>

            <Col>
              <Button
                disabled={checkedCollaboratorsIds?.length === 0}
                dataTestId="quote-collborator-remote-user-button"
                className={isHBUser ? 'hb-button' : ''}
                onClick={handleDeleteCollaborators}
              >
                Remove User
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <div style={{ overflowY: 'scroll', height: '200px' }}>
        {collaborators.length >= 1 &&
          collaborators?.map((collaborator) => (
            <div
              className="paper-container border-muted mb-15 ant-collapse-header guide-card-container py-0"
              key={collaborator.userId}
            >
              <div className="py-10">
                <Row gutter={10} className="flex-nowrap px-15" justify="start">
                  <Col sm={4} className="d-flex align-items-center">
                    <FormCheckBox
                      checkBoxClass="normal-checkbox"
                      className="mb-0 mr-10"
                      checked={checkedCollaboratorsIds.includes(collaborator.userId)}
                      onChange={(e) => handleCheckCollaborators(e, collaborator.userId)}
                    />
                    <div>
                      <p className="m-0 fw-400 f-12 color-label">User Name</p>
                      <p className="mt-1 fw-500 f-13 color-dark">{collaborator.name}</p>
                    </div>
                  </Col>
                  <Col sm={8}>
                    <p className="m-0 fw-400 f-12 color-label">Email </p>
                    <p className="mt-1 fw-600 f-13 color-dark">{collaborator.email}</p>
                  </Col>
                  <Col sm={5}>
                    <p className="m-0 fw-400 f-12 color-label">Role</p>
                    <p className="mt-1 fw-500 f-13 color-dark">{collaborator.role}</p>
                  </Col>
                  <Col sm={4}>
                    <p className="m-0 fw-400 f-12 color-label">Notification e-mails</p>
                    <p className="mt-1 fw-500 f-13 color-dark">{collaborator.notificationsEnabled ? 'On' : 'Off'}</p>
                  </Col>
                  <Col sm={2} className="d-flex justify-content-center hidden-buttons">
                    <IconButton className="ml-16" onClick={() => handleDeleteCollaborators(collaborator.userId)}>
                      <SvgConverter Icon={DeleteIcon} color="iconDanger" width={16} height={16} />
                    </IconButton>
                  </Col>
                </Row>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default memo(QuoteCollaboratorLogs);
